import type { DateTime } from 'luxon'

import type { KeyClubLevel } from '@/utils/keyClub/types'

import type { ProgressionLevel } from '@/queries/constants'

export type LeaderboardItem = {
  rank: number
  statistic: number
  name: string
  isSelf: boolean
  user?: {
    id: number
    avatarUrl: string
    username: string | null
    progressionLevel: ProgressionLevel
  }
}

export enum LeaderboardTimeFrame {
  Week = 'week',
  Month = 'month',
  AllTime = 'all-time',
}

export type LeaderboardTimeFrameWithStartDate =
  | {
      timeFrame: LeaderboardTimeFrame.AllTime
      startDate: null
    }
  | {
      timeFrame: LeaderboardTimeFrame.Month | LeaderboardTimeFrame.Week
      startDate: DateTime
    }

export enum LeaderboardKey {
  PointsLearners = 'sp-learners',
  PointsTutors = 'sp-tutors',
  Badges = 'badges',
  Certifications = 'certifications',
  SessionsAttended = 'sessions-attended',
  SessionsHosted = 'sessions-hosted',
  SuperHelpfuls = 'super-helpfuls',
  LearnersReferred = 'learners-referred',
  TutorsReferred = 'tutors-referred',
  SessionsAudited = 'sessions-audited',
  CertificationsReviewed = 'certifications-reviewed',
  LiveHelpHours = 'live-help-hours',
  TutorsWelcomed = 'tutors-welcomed',
  QuestionsAnswered = 'questions-answered',
  TutoringHours = 'tutoring-hours',
  CountriesReached = 'countries-reached',
  LearnersImpacted = 'learners-impacted',
  PositiveRatings = 'positive-ratings',
}

export type Leaderboard = {
  key: LeaderboardKey
  timeFrame: LeaderboardTimeFrame
  items: LeaderboardItem[]
  keyClubLevel?: KeyClubLevel
  worldId?: number
}

export type LeaderboardPanelInfo = {
  title: string
  action: string
  unit: string
  url?: string
  customPluralUnit?: string
}

// Sometimes, we show the user a "highlight" from their leaderboards,
// i.e. a leaderboard for which their rank or percentile passes a certain
// threshold.
export type LeaderboardHighlight = {
  key: LeaderboardKey
  timeFrame: LeaderboardTimeFrame
  rank?: number
  percentile?: number
}

// Defining here to avoid circular dependency
export const PORTFOLIO_LEADERBOARD_KEYS = [
  LeaderboardKey.SessionsHosted,
  LeaderboardKey.TutoringHours,
  LeaderboardKey.CountriesReached,
  LeaderboardKey.LearnersImpacted,
  LeaderboardKey.PositiveRatings,
  LeaderboardKey.SuperHelpfuls,
] as const

export type PortfolioLeaderboardKey = (typeof PORTFOLIO_LEADERBOARD_KEYS)[number]

export type PortfolioLeaderboardStat = {
  key: PortfolioLeaderboardKey
  percentile: number
}
